import React, { useState } from 'react';
import MedicalRequestPolandForm from './MedicalRequestPolandForm';
import apiClient from '../../../../../Services/ApiClient';
import { useAppContext } from '../../../../../Services/AppContext';
import useNotification from '../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../Hooks/useTranslation';
import useApiClient from '../../../../Base/Hooks/useApiClient';
import Loading from '../../../../../Components/Loading';
import { Meta, useLazyUserMetaQuery } from '../../../../../graphql/graphql';
import { findInMeta } from '../../../../Meta/Services/meta.service';
import { MetaKey } from '../../../../Meta/Enums/MetaKey.enum';
import { useEffectAsync } from '../../../../../helpers';

interface Props {
	patientId: string;
	onSuccess: () => void;
}

const MedicalRequestPoland: React.FC<Props> = ({ patientId, onSuccess }) => {
	const { workplaceId, userData } = useAppContext();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { displayError } = useApiClient();
	const [isLoading, setIsLoading] = useState(false);
	const [doctorIdentificationNumber, setDoctorIdentificationNumber] = useState<
		string | undefined
	>(undefined);

	const [getUserMeta] = useLazyUserMetaQuery();

	useEffectAsync(async () => {
		const data = await getUserMeta({
			id: userData.id,
			useOriginalId: true,
		}).unwrap();

		const identificationNumber = findInMeta({
			meta: data?.user?.meta as Meta[],
			metaKey: MetaKey.IDENTIFICATION_NUMBER,
		});

		if (identificationNumber?.value) {
			setDoctorIdentificationNumber(identificationNumber.value);
		}
	}, []);

	const handleSubmit = async (values: Record<string, any>) => {
		setIsLoading(true);
		const data: object[] = [];

		if (values.diagnosis?.label) {
			values.diagnosis = values.diagnosis.label;
		}

		Object.keys(values).forEach(key => {
			data.push({
				type: key,
				extra: {
					value: values[key],
				},
			});
		});

		try {
			const response = await apiClient.post('/document/', {
				workplaceId: workplaceId,
				userId: patientId,
				createdById: userData.id,
				type: 'MEDICAL_REQUEST_FORM',
				children: data,
			});
			if (response.status === 200) {
				showNotificationSuccess({
					content: t('component.medicalRequestPoland.successMessage'),
				});
				onSuccess();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<MedicalRequestPolandForm
			patientId={patientId}
			onSubmit={handleSubmit}
			doctorIdentificationNumber={doctorIdentificationNumber}
		/>
	);
};

export default MedicalRequestPoland;
